import {
  Component,
  EventEmitter,
  Output,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { ApplicationService, extractDuplicateApplication } from '../../services/application.service';
import { TranslationService } from '../../services/translation.service';
import type { ChatInputTranslations } from '../../i18n/chat-input';
import en from '../../i18n/en/chat-input';
import enDuplicate from '../../i18n/en/duplicate-application';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-chat-input',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './chat-input.component.html',
  styleUrls: ['./chat-input.component.css'],
})
export class ChatInputComponent implements AfterViewInit {
  @Input() applicationId: number | null = null;
  @Output() messageSent = new EventEmitter<any>();
  @Output() applicationCreated = new EventEmitter<number>();
  newMessage: string = '';
  isLoading: boolean = false;

  @ViewChild('messageInput') messageInput!: ElementRef;

  translations: ChatInputTranslations = en as ChatInputTranslations;

  private messageService = inject(MessageService);

  get t(): ChatInputTranslations {
    return this.translations;
  }

  constructor(
    private applicationService: ApplicationService,
    private translationService: TranslationService,
    private cdr: ChangeDetectorRef
  ) {
    const current = this.translationService.getChatInputTranslations();
    if (current) {
      this.translations = current;
    }
    this.translationService.onChange(() => {
      const next = this.translationService.getChatInputTranslations();
      if (next) {
        this.translations = next;
        this.cdr.detectChanges();
      }
    });
  }

  ngAfterViewInit(): void {
    this.focusTextarea();
  }

  sendMessage(): void {
    if (this.newMessage.trim() !== '') {
      this.isLoading = true;
      const messageContent = this.newMessage;

      const handleResponse = (response: any) => {
        console.log('Response received:', response);
        const applicationId = response.assistant_response.application_id;
        const tailoredCoverLetter =
          response.assistant_response.tailored_cover_letter;

        if (applicationId) {
          this.handleMessageSent(messageContent, tailoredCoverLetter);
          this.applicationCreated.emit(applicationId);
          this.applicationId = applicationId;
        } else {
          console.warn('Application ID not found in response', response);
        }
        this.isLoading = false;
      };

      if (this.applicationId !== null) {
        this.applicationService
          .addApplicationMessage(this.applicationId, messageContent)
          .subscribe(handleResponse, (error: any) => {
            console.error('Error adding message to application:', error);
            this.isLoading = false;
          });
      } else {
        this.applicationService
          .addApplication({
            message_type: 'from_user',
            message_text: messageContent,
          })
          .subscribe(handleResponse, (error: any) => {
            console.error('Error posting new application:', error);
            this.isLoading = false;
            this.warnIfDuplicate(error);
          });
      }
    }
  }

  /**
   * chamba-ai#503: POST /from-job-description answers 409 duplicate_application
   * when the user already has an application for this job, and addApplication()
   * opts out of the global error toast - so say so here instead of failing
   * silently. A warning only: "create anyway" makes no sense inside a chat.
   */
  private warnIfDuplicate(error: unknown): void {
    const duplicate = extractDuplicateApplication(error);
    if (!duplicate) {
      return;
    }
    const t = this.translationService.getDuplicateApplicationTranslations() ?? enDuplicate;
    const message = duplicate.matched_by === 'text' ? t.MESSAGE_TEXT : t.MESSAGE_URL;
    this.messageService.add({
      severity: 'warn',
      summary: t.TITLE,
      detail: `${message} (#${duplicate.id})`,
      life: 8000,
    });
  }

  private handleMessageSent(
    messageContent: string,
    tailoredCoverLetter: string | null
  ): void {
    this.messageSent.emit({
      message_type: 'from_user',
      message_text: messageContent,
      tailored_cover_letter: tailoredCoverLetter,
      date: this.formatDate(new Date()),
    });
    this.newMessage = '';
    this.focusTextarea();
  }

  private formatDate(date: Date): string {
    return date.toISOString();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private focusTextarea(): void {
    this.messageInput.nativeElement.focus();
  }
}
